import captureFubUserWithConsent from 'pages/api/consent-disclaimers/consentApiClient';
import type { TenantTypes } from '@tenants';
import type { IUserLocation } from 'utils/user-location';
import type { ThemeNames } from '@tenants/types';
import type { CountryCode } from 'types/countries';
import { CountryCodeList } from 'types/countries';

/**
 * Consent Capture Service
 * 
 * A self-contained service that manages all consent-related logic.
 * Think of it like a third-party widget - you just plug it into your UI,
 * and it handles everything internally:
 * - Whether to show consent disclaimers (TCPA logic)
 * - When to capture consent
 * - How to format the API request
 * - Whether to make the API call at all
 * 
 * The UI doesn't need to know any of these details.
 */
export class ConsentCaptureService {
  private readonly shouldShowDisclaimers: boolean;

  constructor(
    private readonly tenant: TenantTypes,
    private readonly userLocation: IUserLocation | null,
    private readonly consentDisclaimerText: string
  ) {
    // Service determines on initialization whether disclaimers should be shown
    this.shouldShowDisclaimers = this.requiresTcpaDisclaimer(
      this.tenant,
      this.userLocation?.countryCode
    );
  }

  /**
   * Determines if TCPA disclaimers are required based on tenant configuration and user location.
   * 
   * TCPA (Telephone Consumer Protection Act) disclaimers are required for US users.
   * This method encapsulates the logic for determining when to show these disclaimers.
   * 
   * Rules:
   * - If tenant's default country is US → always show (US-only tenant like expRealtyUS)
   * - If tenant supports multiple countries including US → show only for US users
   * - If tenant doesn't support US → never show (Canada-only tenant like expRealtyCA)
   */
  private requiresTcpaDisclaimer(tenant: TenantTypes, userCountryCode?: CountryCode | null): boolean {
    const tenantDefaultCountry = tenant.localization.defaultCountry;
    const tenantSupportedCountries = tenant.localization.countries;

    // If tenant's default country is US, always show disclaimers
    if (tenantDefaultCountry === CountryCodeList.UNITED_STATES) {
      return true;
    }

    // If tenant doesn't support US at all, never show disclaimers
    if (!tenantSupportedCountries.includes(CountryCodeList.UNITED_STATES)) {
      return false;
    }

    // Multi-country tenant: show disclaimers only for US users
    return userCountryCode === CountryCodeList.UNITED_STATES;
  }

  /**
   * Tells the UI whether to show TCPA consent disclaimers
   */
  shouldShow(): boolean {
    return this.shouldShowDisclaimers;
  }

  /**
   * Captures consent if needed.
   * The service decides internally whether to actually make the API call.
   * The UI just calls this method and doesn't worry about the details.
   */
  async capture(data: {
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    listingId?: number;
    listingUrl?: string;
  }): Promise<void> {
    // If we're not showing disclaimers, don't capture
    if (!this.shouldShowDisclaimers) {
      return;
    }

    // Service builds the complete payload internally
    const payload = {
      firstName: data.firstName,
      lastName: data.lastName,
      email: data.email,
      phone: data.phone,
      source: this.tenant.theme.id as ThemeNames,
      userIp: this.userLocation?.ip || '',
      hasConsented: true,
      consentDisclaimerText: this.consentDisclaimerText,
      countryCode: this.userLocation?.countryCode,
      createdAt: new Date().toISOString(),
      ...(data.listingId && { listingId: data.listingId }),
      ...(data.listingUrl && { listingUrl: data.listingUrl }),
    };

    // Service makes the API call
    await captureFubUserWithConsent(payload);
  }
}

/**
 * Factory function to create a consent service instance
 */
export function createConsentService(
  tenant: TenantTypes,
  userLocation: IUserLocation | null,
  consentDisclaimerText: string
): ConsentCaptureService {
  return new ConsentCaptureService(
    tenant,
    userLocation,
    consentDisclaimerText
  );
}


